import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema } from '../common/index.js';
import { NotaryInquirySchema } from './biens.js';
import { BillingClientViewSchema } from './facturation.js';
import { PersonClercViewSchema, PersonFamilyViewSchema } from './genealogie.js';
import { DocumentRequirementSchema } from './documents.js';
import { ConsentPurposeSchema } from './confidentialite.js';

/**
 * Vues externes : portail client (mandant, héritier), espace clerc, espace notaire (architecture §3.3 portal, clerc, notaire).
 * Correction n° 7 de la synthèse : une seule liste d'étapes client, mêmes libellés sur M4, X13 et X14.
 */

export const ETAPES_CLIENT = [
  { code: 'famille', libelle: 'Famille identifiée' },
  { code: 'procurations', libelle: 'Procurations signées' },
  { code: 'notaire', libelle: 'Dossier ouvert chez le notaire' },
  { code: 'etat_civil', libelle: "Pièces d'état civil" },
  { code: 'titres', libelle: 'Titres de propriété' },
  { code: 'notoriete', libelle: 'Acte de notoriété' },
  { code: 'partage', libelle: 'Partage ou vente' },
  { code: 'attestation', libelle: 'Attestation immobilière publiée' },
  { code: 'cloture', libelle: 'Dossier terminé' },
] as const;

export const EtapeClientSchema = z
  .enum(['famille', 'procurations', 'notaire', 'etat_civil', 'titres', 'notoriete', 'partage', 'attestation', 'cloture'])
  .meta({ id: 'EtapeClient' });

export const NextActionSchema = z
  .strictObject({
    kind: z.enum(['piece', 'paiement', 'consentement', 'signature', 'aucune']),
    label: z.string().min(1),
    ctaLabel: z.string().min(1),
    alternativeLabel: z.string().nullable(),
    /** Pièce attendue de la personne du compte quand `kind = 'piece'` (RFC-DEF-PORTAIL-PIECE) ; additif, donc optionnel. */
    requirementId: IdSchema.nullable().optional(),
  })
  .meta({ id: 'NextAction', description: 'Une seule chose à faire, avec un bouton.' });

export const PortalHomeSchema = z
  .strictObject({
    dossierTitle: z.string().min(1),
    greeting: z.string().min(1),
    steps: z.array(
      z.strictObject({
        code: EtapeClientSchema,
        label: z.string().min(1),
        state: z.enum(['fait', 'en_cours', 'a_venir']),
        detail: z.string().nullable(),
      }),
    ),
    nextAction: NextActionSchema,
    branch: z.array(PersonFamilyViewSchema),
    billing: BillingClientViewSchema,
    signature: z.literal('Cabinet MAAT'),
  })
  .meta({ id: 'PortalHome', description: 'Empreinte de progression et action unique (M4, X13, X14).' });

export const PortalTimelineEntrySchema = z
  .strictObject({ at: InstantSchema, kind: z.enum(['message', 'appel', 'piece', 'paiement', 'consentement']), summary: z.string().min(1) })
  .meta({ id: 'PortalTimelineEntry', description: 'Ses échanges seulement, jamais les coulisses.' });

export const PortalPrivacySchema = z
  .strictObject({
    consents: z.array(z.strictObject({ purpose: ConsentPurposeSchema, granted: z.boolean(), capturedAt: InstantSchema })),
    contactOnlyByCabinet: z.boolean(),
    preferredChannel: z.enum(['whatsapp', 'email', 'telephone', 'courrier']).nullable(),
    dataRequestUrl: z.string().min(1),
  })
  .meta({ id: 'PortalPrivacy', description: 'Mes données et ma confidentialité (M7).' });

export const PortalPrivacyUpdateSchema = z
  .strictObject({
    consents: z.array(z.strictObject({ purpose: ConsentPurposeSchema, granted: z.boolean() })).optional(),
    preferredChannel: z.enum(['whatsapp', 'email', 'telephone', 'courrier']).nullable().optional(),
  })
  .meta({ id: 'PortalPrivacyUpdate' });

export const PortalPieceUploadSchema = z
  .strictObject({ requirementId: IdSchema, mime: z.string().min(3), size: z.number().int().min(1) })
  .meta({ id: 'PortalPieceUpload' });

export const PortalPieceDelegationSchema = z
  .strictObject({ requirementId: IdSchema })
  .meta({ id: 'PortalPieceDelegation', description: '« Je ne l’ai pas : demandez-le pour moi » (RFC-DEF-PORTAIL-PIECE).' });

export const PortalPieceDelegationResultSchema = z
  .strictObject({ requirementId: IdSchema, delegatedRequest: z.literal(true) })
  .meta({ id: 'PortalPieceDelegationResult', description: 'La pièce est demandée par le cabinet.' });

export const PortalUploadSessionSchema = z
  .strictObject({ uploadUrl: z.string().min(1), expiresAt: InstantSchema, maxSizeBytes: z.number().int().min(1) })
  .meta({ id: 'PortalUploadSession' });

export const ClercDossierSchema = z
  .strictObject({
    id: IdSchema,
    reference: z.string().min(1),
    title: z.string().min(1),
    completeness: z.number().int().min(0).max(100),
    readyForOpening: z.boolean(),
    inquiries: z.array(NotaryInquirySchema),
    missingPieces: z.array(DocumentRequirementSchema),
  })
  .meta({ id: 'ClercDossier', description: 'Dossier transmis à l’étude (X11).' });

export const NotaireDossierSchema = z
  .strictObject({
    id: IdSchema,
    reference: z.string().min(1),
    title: z.string().min(1),
    living: z.array(PersonClercViewSchema),
    deceased: z.array(PersonClercViewSchema),
    acts: z.array(
      z.strictObject({
        label: z.string().min(1),
        status: z.enum(['a_preparer', 'en_cours', 'signe', 'publie']),
        dueAt: LocalDateSchema.nullable(),
      }),
    ),
    titles: z.array(
      z.strictObject({ assetLabel: z.string().min(1), titleStatus: z.enum(['trouve', 'recherche', 'introuvable', 'prescription']) }),
    ),
  })
  .meta({ id: 'NotaireDossier', description: 'Tableaux des vivants et des défunts, titres, actes (X12).' });

export const BetaQuestionViewSchema = z
  .object({
    id: z.string().regex(/^BETA-\d{2}$/),
    key: z.string().min(1),
    question: z.string().min(1),
    options: z.array(z.object({ code: z.string(), label: z.string() })).min(2),
    default: z.string().nullable(),
  })
  .meta({ id: 'BetaQuestionView', description: 'Question BETA due pour le contexte courant (pop-up « BETA test »).' });

export const BetaAnswerSchema = z
  .object({ option: z.string().min(1), comment: z.string().optional(), context: z.record(z.string(), z.string()).optional() })
  .meta({ id: 'BetaAnswer' });

export const LearnedSettingSchema = z
  .object({ key: z.string().min(1), value: z.unknown(), updatedAt: InstantSchema, updatedByDisplayName: z.string() })
  .meta({ id: 'LearnedSetting', description: 'Réglage appris (page « Réglages appris »).' });

export const SyncPushSchema = z
  .object({
    deviceId: z.string().min(1),
    mutations: z
      .array(
        z.object({
          mutationId: IdSchema,
          entityType: z.string().min(1),
          entityId: IdSchema,
          baseVersion: z.number().int().min(0),
          op: z.record(z.string(), z.unknown()),
        }),
      )
      .min(1),
  })
  .meta({ id: 'SyncPush' });

export const SyncPushResultSchema = z
  .object({
    results: z.array(
      z.object({
        mutationId: IdSchema,
        result: z.enum(['applique', 'conflit', 'rejete']),
        conflict: z.record(z.string(), z.unknown()).nullable(),
      }),
    ),
  })
  .meta({ id: 'SyncPushResult' });

export const SyncPullSchema = z
  .object({
    changes: z.array(
      z.object({
        seq: z.string().regex(/^\d+$/),
        entityType: z.string(),
        entityId: IdSchema,
        op: z.enum(['upsert', 'delete']),
        rowVersion: z.number().int(),
      }),
    ),
    cursor: z.string(),
  })
  .meta({ id: 'SyncPull' });

export const WebhookPayloadSchema = z
  .record(z.string(), z.unknown())
  .meta({ id: 'WebhookPayload', description: 'Charge brute, vérifiée par signature.' });
